import type { ComponentChildren } from 'preact';

/** A section label whose colour is independent of the `◄ ACTIVE` marker.
 *
 *  `chrome/SectionLabel.jsx` ties the two together: one `active` prop decides
 *  both whether the label is teal and whether it carries `◄ ACTIVE`. That is
 *  correct on the Daily Log, where two regions compete for focus and the frame
 *  shows `SCHEDULE` muted beside `RAPID LOG ◄ ACTIVE`. It is wrong on any list
 *  page, where the frames show every label teal and no region is a focus target
 *  — the Filters index draws `STREAMS`, `PROJECTS`, `CONTEXTS` and `ARCHIVED`
 *  all in teal, and passing `active` to get that colour would stamp four
 *  meaningless `◄ ACTIVE` badges across the page.
 *
 *  Same spec as the component it corrects — small, letter-spaced, uppercase,
 *  teal, with an optional muted note. Reported upstream; when `SectionLabel`
 *  separates colour from focus, delete this.
 */
export function SectionHeading({
  children,
  note,
  right,
  marker = 'ACTIVE',
  /** Teal by default, because that is what a section label is. Muted only where
   *  a focus model makes this region the inactive one. */
  muted = false,
  active = false,
}: {
  children: ComponentChildren;
  note?: string;
  /** Pushed to the far edge of the row, as the frames set `NEWEST FIRST` and a
   *  count against the opposite margin from the label. The kit's `SectionLabel`
   *  has a `right` prop for this and nothing ever used it, which is why every
   *  note in this app was crammed up against its label instead. */
  right?: string;
  /** The word the active marker carries. `ACTIVE` on the Daily Log's regions,
   *  `OPEN` on the book whose entries are showing. */
  marker?: string;
  muted?: boolean;
  active?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 'var(--bujo-space-2)',
        padding: 'var(--bujo-space-4) 0 var(--bujo-space-2)',
        // In a narrow column — two days, two lanes on a split-screen iPad —
        // these three parts do not fit on one line. Without this the flex items
        // shrink and the words break *inside* them, so the section reads
        // `RAPID` / `LOG` over two lines with `ACTIVE` stacked beside it. Whole
        // parts move to the next line instead; each one stays on its own.
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/var(--bujo-lh-tight) var(--bujo-font-mono)',
          letterSpacing: 'var(--bujo-ls-label)',
          color: muted ? 'var(--bujo-text-meta)' : 'var(--bujo-label)',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </span>
      {active ? (
        <span
          style={{
            font: 'var(--bujo-fw-bold) 9.5px/var(--bujo-lh-tight) var(--bujo-font-mono)',
            letterSpacing: 'var(--bujo-ls-badge)',
            color: 'var(--bujo-label)',
            whiteSpace: 'nowrap',
          }}
        >
          {'◄'} {marker}
        </span>
      ) : null}
      {note ? (
        <span
          style={{
            font: 'var(--bujo-fw-regular) 9.5px/var(--bujo-lh-tight) var(--bujo-font-mono)',
            color: 'var(--bujo-text-meta)',
            // The note is the one part that must stay breakable. It is a
            // compound string — `READ-ONLY · 7 EVENTS · STALE · AS OF …` — and
            // longer than a narrow lane on its own, so `nowrap` here does not
            // keep it tidy, it makes it overflow the lane and paint across the
            // rapid log beside it. The label and the badge are short and atomic;
            // this is not.
            minWidth: 0,
          }}
        >
          {note}
        </span>
      ) : null}
      {right ? (
        <span
          style={{
            marginLeft: 'auto',
            font: 'var(--bujo-fw-regular) 9.5px/var(--bujo-lh-tight) var(--bujo-font-mono)',
            letterSpacing: 'var(--bujo-ls-badge)',
            color: 'var(--bujo-text-meta)',
            whiteSpace: 'nowrap',
          }}
        >
          {right}
        </span>
      ) : null}
    </div>
  );
}
